import { useEffect, useMemo, useRef, useState } from 'react';

interface IUseIntegerCounterAnimation {
  animatedValue: number;
}

interface IUseIntegerCounterAnimationProps {
  initial: number;
  target: number;
  isAnimated?: boolean;
  duration?: number;
}

const useIntegerAnimation = ({
  initial = 0,
  target = 0,
  isAnimated = true,
  duration = 2000, //milliseconds
}: IUseIntegerCounterAnimationProps): IUseIntegerCounterAnimation => {
  const [animatedValue, setAnimatedValue] = useState<number>(initial);
  const isRunning = useMemo(
    () => isAnimated && animatedValue < target,
    [isAnimated, animatedValue]
  );

  const requestRef = useRef<number | null>(null);
  const startTimeRef = useRef<number | null>(null);

  const animate = (timestamp: number) => {
    if (!startTimeRef.current) {
      startTimeRef.current = timestamp;
    }

    const elapsed = timestamp - startTimeRef.current;
    const progress = Math.min(elapsed / duration, 1);

    setAnimatedValue(() => {
      const newValue = Math.floor(initial + (target - initial) * progress);

      if (newValue >= target) {
        return target;
      }

      return newValue;
    });

    if (progress < 1) {
      requestRef.current = requestAnimationFrame(animate);
    }
  };

  useEffect(() => {
    if (isRunning && !requestRef.current) {
      requestRef.current = requestAnimationFrame(animate);
    } else if (!isRunning && requestRef.current) {
      cancelAnimationFrame(requestRef.current);
    }

    return () => {
      if (requestRef.current) {
        cancelAnimationFrame(requestRef.current);
      }
    };
  }, [isRunning]);

  return { animatedValue: Number(animatedValue) };
};

export default useIntegerAnimation;
